import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO/SEO';

const ServicePage = () => {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'company' ? 'company' : 'sr';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    fetch('/data/downloads.json?t=' + Date.now())
      .then((r) => r.ok ? r.json() : [])
      .then(setDownloads)
      .catch(() => setDownloads([]));
  }, []);

  const getDownloadUrl = (tab) => {
    const item = downloads.find((d) => d.tab === tab);
    return item?.fileUrl || '';
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="サービス紹介｜勤怠管理・労務管理の機能と特徴"
        description="SrWithのサービス詳細。就業規則の事前登録、36協定の自動監視、変形労働時間制対応など、社労士の17年の実務経験を活かした予防型労務管理の機能をご紹介。社労士・企業双方にメリットのあるプラットフォームです。"
      />

      {/* Hero Section - シンプル */}
      <section className="relative overflow-hidden bg-gradient-to-br from-pink-beige to-white">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-48 translate-x-48 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full translate-y-40 -translate-x-40 blur-2xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            
            {/* 左側コンテンツ */}
            <div className="space-y-6 text-center lg:text-left">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-brown leading-tight">
                社労士も企業も、<br />
                みんなが嬉しい<br />
                労務プラットフォーム
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-gray-600 font-light leading-relaxed">
                <span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>で今日よりも明日のほうがいい会社を作る
              </p>
            </div>

            {/* 右側写真エリア */}
            <div className="relative">
              <div className="aspect-[4/3] relative">
                <div className="absolute inset-0 bg-gradient-to-br from-pink-beige/20 to-transparent rounded-3xl transform rotate-2"></div>
                <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl transform -rotate-1 hover:rotate-0 transition-transform duration-700">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/hero-solution.jpg`}
                    alt="スッキリ解決した明るいオフィス環境"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-full h-full items-center justify-center text-gray-400 bg-gray-50">
                    解決後のイメージ写真
                  </div>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="absolute -bottom-6 -left-4 bg-white rounded-2xl p-4 shadow-xl border border-gray-100">
                <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">労務管理</div>
                <div className="text-2xl font-bold text-accent">安心・効率化</div>
              </div>
              
            </div>
          </div>
          
          {/* タブ切り替えボタン - 中央寄せで最下部に配置 */}
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setActiveTab('sr')}
              className={`px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 ${
                activeTab === 'sr'
                  ? 'bg-rose-100 text-brown shadow-xl scale-105 border-2 border-rose-300'
                  : 'bg-white/30 text-brown hover:bg-white/50 hover:shadow-lg border-2 border-gray-200'
              }`}
            >
              社労士の方
            </button>
            <button
              onClick={() => setActiveTab('company')}
              className={`px-8 py-4 rounded-2xl font-semibold text-lg transition-all duration-300 ${
                activeTab === 'company'
                  ? 'bg-rose-100 text-brown shadow-xl scale-105 border-2 border-rose-300'
                  : 'bg-white/30 text-brown hover:bg-white/50 hover:shadow-lg border-2 border-gray-200'
              }`}
            >
              企業・事業主の方
            </button>
          </div>
        </div>
      </section>

      {/* メインコンテンツ - タブに応じて切り替え */}
      {activeTab === 'company' ? (
        // 企業向けセクション
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            
            {/* セクションタイトル */}
            <div className="text-center mb-20">
              <span className="text-lg font-medium text-pink-beige uppercase tracking-wide">For Companies</span>
              <h2 className="text-4xl md:text-5xl font-black text-brown mt-4 mb-8">
                企業・事業主の皆様へ
              </h2>
              <p className="text-xl md:text-2xl text-gray-text max-w-3xl mx-auto">
                こんな労務管理の悩み、ありませんか？
              </p>
            </div>

            {/* 企業の悩み */}
            <div className="mb-20">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="p-8 rounded-2xl text-center" style={{backgroundColor: '#F5F1F0'}}>
                  <h3 className="text-xl font-bold mb-4" style={{color: '#8B4513'}}>⚠️ 法令違反のリスク</h3>
                  <ul className="space-y-2" style={{color: '#A0522D'}}>
                    <li>• 36協定の未締結</li>
                    <li>• 残業代の計算ミス</li>
                    <li>• 就業規則の実態乖離</li>
                    <li>• 労働基準監督署の調査不安</li>
                  </ul>
                </div>
                
                <div className="p-8 rounded-2xl text-center" style={{backgroundColor: '#E8D7D1'}}>
                  <h3 className="text-xl font-bold mb-4" style={{color: '#6B4423'}}>📋 手続きの負担</h3>
                  <ul className="space-y-2" style={{color: '#8B4513'}}>
                    <li>• 社会保険手続きの複雑さ</li>
                    <li>• 給与計算に毎月苦労</li>
                    <li>• 年末調整の大変さ</li>
                    <li>• 助成金の見落とし</li>
                  </ul>
                </div>
                
                <div className="p-8 rounded-2xl text-center" style={{backgroundColor: '#F5F1F0'}}>
                  <h3 className="text-xl font-bold mb-4" style={{color: '#8B4513'}}>💼 経営への影響</h3>
                  <ul className="space-y-2" style={{color: '#A0522D'}}>
                    <li>• 本業に集中できない</li>
                    <li>• 専門知識不足への不安</li>
                    <li>• 人材定着率の悪化</li>
                    <li>• 労働紛争への恐れ</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 従来の解決策とSrWithを使ったら */}
            <div className="mb-20">
              <h3 className="text-3xl md:text-4xl font-bold text-center text-brown mb-16">
                従来の解決策と<span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>を使ったら
              </h3>
              
              {/* サブタイトル行 */}
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-2 items-center mb-8">
                <h4 className="text-2xl font-bold text-gray-700 text-center">従来の解決策</h4>
                <div className="hidden lg:block w-8"></div>
                <h4 className="text-2xl font-bold text-center"><span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>を使ったら</h4>
              </div>

              <div className="space-y-8">
                {/* 1行目 */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-2 items-center">
                  <div className="bg-gray-100 p-4 rounded-xl relative h-40 flex flex-col justify-between text-center">


                    <div>
                      <h5 className="font-bold text-gray-text mb-3 text-xl">自社での労務管理</h5>
                      <p className="text-gray-600 text-sm">経営者や事務員が独学で対応</p>
                    </div>
                    <div className="text-red-600 text-sm">
                      ➡ 専門知識不足、法改正対応遅れ、ミス多発
                    </div>
                  </div>
                  
                  <div className="hidden lg:block">
                    <svg className="w-8 h-8 text-pink-beige" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                    </svg>
                  </div>
                  
                  <div className="bg-rose-100 p-4 rounded-xl h-40 flex flex-col justify-between border-2 border-rose-300 text-center">


                    <div>
                      <h5 className="font-bold text-gray-text mb-3 text-xl">🛡️ 予防型アプローチ</h5>
                      <p className="text-gray-text text-sm mb-3">問題が起きる前にアラートで予防</p>
                    </div>
                    <div className="font-semibold text-gray-text text-sm">
                      ・事前登録された協定・規程で自動チェック<br />
                      ・リアルタイムでの法令違反予防
                    </div>
                  </div>
                </div>

                {/* 2行目 */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-2 items-center">
                  <div className="bg-gray-100 p-4 rounded-xl relative h-40 flex flex-col justify-between text-center">

                    <div>
                      <h5 className="font-bold text-gray-text mb-3 text-xl">従来型の社労士契約</h5>
                      <p className="text-gray-600 text-sm">月1回訪問の手続き代行中心</p>
                    </div>
                    <div className="text-red-600 text-sm">
                      ➡ 事後対応、リアルタイム性なし、根本解決されない
                    </div>
                  </div>
                  
                  <div className="hidden lg:block">
                    <svg className="w-8 h-8 text-pink-beige" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                    </svg>
                  </div>
                  
                  <div className="bg-rose-100 p-4 rounded-xl h-40 flex flex-col justify-between border-2 border-rose-300 text-center">

                    <div>
                      <h5 className="font-bold text-gray-text mb-3 text-xl">🤝 統合型サポート</h5>
                      <p className="text-gray-text text-sm mb-3">システム×社労士の専門知識</p>
                    </div>
                    <div className="font-semibold text-gray-text text-sm">
                      ・17年の実務経験が詰まったシステム<br />
                      ・いつでも相談できる社労士のサポート
                    </div>
                  </div>
                </div>

                {/* 3行目 */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-2 items-center">
                  <div className="bg-gray-100 p-4 rounded-xl relative h-40 flex flex-col justify-between text-center">


                    <div>
                      <h5 className="font-bold text-gray-text mb-3 text-xl">一般的な勤怠システム</h5>
                      <p className="text-gray-600 text-sm">時間管理のみのシステム導入</p>
                    </div>
                    <div className="text-red-600 text-sm">
                      ➡ 法令チェックなし、専門知識不足、違反リスク残存
                    </div>
                  </div>
                  
                  <div className="hidden lg:block">
                    <svg className="w-8 h-8 text-pink-beige" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                    </svg>
                  </div>
                  
                  <div className="bg-rose-100 p-4 rounded-xl h-40 flex flex-col justify-between border-2 border-rose-300 text-center">

                    <div>
                      <h5 className="font-bold text-gray-text mb-3 text-xl">📈 継続改善型</h5>
                      <p className="text-gray-text text-sm mb-3">一緒に会社を良くしていく</p>
                    </div>
                    <div className="font-semibold text-gray-text text-sm">
                      ・ 月次での労務状況分析・改善提案<br />
                      ・「いい会社」への継続的な成長支援
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center bg-pink-beige/5 p-12 rounded-3xl">
              <h3 className="text-3xl font-bold text-brown mb-6">
                労務管理を「負担」から「競争力」に変えませんか？
              </h3>
              <p className="text-xl text-gray-text mb-8 max-w-2xl mx-auto">
                SrWithで、法令遵守しながら従業員が安心して働ける「いい会社」を実現しましょう
              </p>
              <a href={getDownloadUrl('client')} download className={`inline-block bg-pink-beige text-white px-12 py-4 rounded-2xl text-lg font-semibold hover:bg-brown transition-all duration-300 transform hover:scale-105 shadow-lg ${getDownloadUrl('client') ? '' : 'opacity-50 pointer-events-none'}`}>
                企業向け資料をダウンロード
              </a>
            </div>
          </div>
        </section>
      ) : (
        // 社労士向けセクション（AcademyLP内容）
        <div className="font-noto text-lp-ink">
          {/* ─── HERO ─── */}
          <section className="bg-lp-dark relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_60%_40%,rgba(201,122,122,0.12)_0%,transparent_65%)] pointer-events-none" />
            <div className="relative max-w-[880px] mx-auto px-5 md:px-12 py-24 md:py-[96px] text-center">
              <p className="text-[11px] font-bold text-lp-warm-rose-mid tracking-[0.16em] uppercase mb-6">
                社労士のための実行型プラットフォーム
              </p>
              <h1 className="font-noto-serif text-[clamp(28px,4.5vw,52px)] font-black leading-[1.45] text-white mb-6 max-w-[760px] mx-auto">
                社労士が、経営の意思決定に関わる存在になる。
              </h1>
              <p className="text-[15px] text-white/60 leading-[1.9] max-w-[560px] mx-auto mb-10">
                手続きからコンサルへ。SrWithは社労士が「経営者に頼られる存在」へシフトするための学び・ツール・仲間を提供します。
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                <Link
                  to="/lp/academy"
                  className="px-9 py-[15px] bg-lp-warm-rose text-white text-[14px] font-bold rounded hover:bg-[#b86666] transition-all hover:-translate-y-px"
                >
                  アカデミーを見る
                </Link>
                <a
                  href="/contact"
                  className="px-9 py-[15px] border-[1.5px] border-white/30 text-white text-[14px] font-bold rounded hover:border-white transition-all"
                >
                  無料個別相談を予約する
                </a>
              </div>
            </div>
          </section>

          {/* ─── PROBLEM & SOLUTION ─── */}
          <section className="bg-lp-warm-cream">
            <div className="max-w-[880px] mx-auto px-5 md:px-12 py-20">
              <h2 className="font-noto-serif text-[clamp(22px,3vw,32px)] font-black text-lp-ink leading-[1.5] mb-4 text-center">
                手続き中心モデルの限界と、<br className="hidden md:block" />これからの社労士に求められること
              </h2>
              <div className="grid md:grid-cols-2 gap-12 mt-12">
                <div className="space-y-3">
                  {[
                    { icon: '⏱', title: '工数の割に報酬が上がらない' },
                    { icon: '🤖', title: 'AI・SaaSによる業務代替' },
                    { icon: '🔄', title: '他事務所との差別化が難しい' },
                    { icon: '🚪', title: '経営の中心に入り込めない' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3 bg-white rounded-lg px-4 py-4 border border-lp-warm-border">
                      <span className="w-8 h-8 rounded-full bg-lp-warm-rose-light flex items-center justify-center flex-shrink-0 text-[14px]">{item.icon}</span>
                      <p className="text-[13px] text-lp-ink2 leading-[1.7] pt-1">
                        <strong className="text-lp-ink block text-[13px] mb-0.5">{item.title}</strong>
                      </p>
                    </div>
                  ))}
                </div>
                <div className="bg-lp-ink rounded-xl p-7">
                  <h3 className="font-noto-serif text-[18px] font-black text-white mb-4 leading-[1.5]">
                    SrWithの解決策
                  </h3>
                  <div className="space-y-2.5">
                    {[
                      'お困りごとの"上位"にアプローチする力',
                      'データ×経営意思決定を支援するスキル',
                      '課題の発見・解決・成長支援の型',
                      '事務作業でなく、コンサルで契約できる仕組み',
                      '時間単価5万円を目指せる収益モデル',
                    ].map((text) => (
                      <p key={text} className="flex gap-2.5 text-[13px] text-white/80 leading-[1.7]">
                        <span className="text-lp-warm-rose-mid font-bold flex-shrink-0">✓</span>
                        {text}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ─── WHAT IS SrWith ─── */}
          <section className="bg-white">
            <div className="max-w-[880px] mx-auto px-5 md:px-12 py-20">
              <h2 className="font-noto-serif text-[clamp(22px,3vw,32px)] font-black text-lp-ink leading-[1.5] mb-12 text-center">
                SrWithは3つの柱で構成されています
              </h2>
              <div className="grid md:grid-cols-3 gap-5">
                {[
                  {
                    num: '01',
                    tag: 'LEARN & PRACTICE',
                    title: 'アカデミー',
                    desc: '6ヶ月のプログラムで、コンサルになるために必要なスキル・営業法・提案の型を習得。学びを「仕組み」にします。',
                    link: '/lp/academy',
                    linkText: 'アカデミー詳細を見る',
                  },
                  {
                    num: '02',
                    tag: 'TOOL',
                    title: 'システム',
                    desc: '就業規則・労働条件通知書をマスタとした勤怠管理、適正給与・人件費マネジメント・経営数字の可視化を実現するツール。経営者への提案を「見える化」します。',
                  },
                  {
                    num: '03',
                    tag: 'COMMUNITY',
                    title: 'パートナーコミュニティ',
                    desc: '全国のSrWithパートナー社労士との月次勉強会・事例共有。仲間と切磋琢磨しながら成長できる環境です。',
                  },
                ].map((card) => {
                  const isAcademy = card.num === '01';
                  return (
                    <div key={card.tag} className={`border rounded-xl p-6 ${isAcademy ? 'bg-lp-charcoal border-lp-charcoal' : 'bg-white border-lp-warm-border'}`}>
                      <p className={`font-noto-serif text-[36px] font-black leading-none mb-2.5 ${isAcademy ? 'text-lp-rose' : 'text-lp-warm-rose-mid'}`}>{card.num}</p>
                      <span className={`inline-block text-[10px] font-bold px-2.5 py-1 rounded-full mb-2.5 tracking-[0.08em] ${isAcademy ? 'text-white bg-white/15' : 'text-lp-warm-rose bg-lp-warm-rose-light'}`}>{card.tag}</span>
                      <h3 className={`text-[15px] font-bold mb-2 ${isAcademy ? 'text-white' : 'text-lp-ink'}`}>{card.title}</h3>
                      <p className={`text-[12px] leading-[1.8] ${isAcademy ? 'text-white/70' : 'text-lp-ink2'}`}>{card.desc}</p>
                      {card.link && (
                        <Link to={card.link} className={`inline-block mt-3 text-[12px] font-bold hover:underline ${isAcademy ? 'text-lp-rose' : 'text-lp-warm-rose'}`}>
                          {card.linkText}
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* ─── SYSTEM ─── */}
          <section className="bg-white">
            <div className="max-w-[880px] mx-auto px-5 md:px-12 py-20">
              <p className="text-[11px] font-bold text-lp-warm-rose tracking-[0.14em] uppercase mb-3 text-center">SYSTEM</p>
              <h2 className="font-noto-serif text-[clamp(22px,3vw,32px)] font-black text-lp-ink leading-[1.5] mb-4 text-center">
                経営数字を「見える化」して、<br className="hidden md:block" />経営者との対話を深める
              </h2>
              <p className="text-center text-[13px] text-lp-ink2 leading-[1.9] max-w-[600px] mx-auto mb-12">
                適正給与・賞与・人員数の算出から、人件費の予実管理まで。社労士が経営者の意思決定をサポートするために必要な数字を、わかりやすく提供します。
              </p>

              <div className="grid md:grid-cols-2 gap-3 mb-10">
                {[
                  { title: '適正給与算出', desc: '職種・経験から導く給与の根拠。「いくらにすればいい？」をなくす' },
                  { title: '適正賞与・人件費の予実管理', desc: '利益から逆算した人件費設計。利益を守りながら、納得感のある報酬へ' },
                  { title: '適正人員数検討', desc: '企業の未来から逆算した人員計画。採用・離職の判断に根拠を持てる' },
                  { title: '社員と企業のベクトルを揃える', desc: '社労士のアドバイスとセミナーで、人の問題を「起きてから」ではなく「起きる前に」' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-lp-warm-rose flex-shrink-0 mt-[7px]" />
                    <div>
                      <p className="text-[13px] text-lp-ink2 leading-[1.7]"><strong className="text-lp-ink">{item.title}</strong> ─ {item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-lp-warm-cream border border-lp-warm-border rounded-xl p-8 text-center">
                <p className="text-[11px] font-bold text-lp-ink3 border border-lp-warm-border rounded-full inline-block px-3.5 py-1 mb-5">適正3セット</p>
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    { title: '適正給与', sub: '経験・職種から算出' },
                    { title: '適正賞与', sub: '利益から逆算' },
                    { title: '適正人員数', sub: '未来から逆算' },
                  ].map((item) => (
                    <div key={item.title} className="bg-white border border-lp-warm-border rounded-lg p-3 text-center">
                      <p className="text-[12px] font-bold text-lp-ink">{item.title}</p>
                      <p className="text-[11px] text-lp-ink2 mt-0.5">{item.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ─── PRICE ─── */}
          <section className="bg-lp-warm-cream">
            <div className="max-w-[880px] mx-auto px-5 md:px-12 py-20">
              <p className="text-[11px] font-bold text-lp-warm-rose tracking-[0.14em] uppercase mb-3 text-center">PRICE</p>
              <h2 className="font-noto-serif text-[clamp(22px,3vw,32px)] font-black text-lp-ink leading-[1.5] mb-12 text-center">
                料金プラン
              </h2>
              <div className="bg-lp-ink rounded-xl p-7 max-w-[560px] mx-auto text-center">
                <p className="text-[10px] font-bold text-lp-warm-rose-mid tracking-[0.1em] mb-2">システム利用プラン</p>
                <div className="font-noto-serif text-[28px] font-black text-white mb-1">¥19,800</div>
                <p className="text-[11px] text-white/50 mb-1">月額（税抜）/ 100IDまで</p>
                <p className="text-[11px] text-white/40 mb-4">101ID以降：1IDにつき月額100円</p>
                <hr className="border-white/15 mb-4" />
                <div className="space-y-2 text-left max-w-[280px] mx-auto mb-6">
                  {[
                    'SrWithシステムの利用',
                    '月次勉強会への参加',
                    'パートナーコミュニティ',
                  ].map((item) => (
                    <p key={item} className="flex gap-2 text-[12px] text-white/70 leading-[1.6]">
                      <span className="text-lp-warm-rose-mid font-bold flex-shrink-0">✓</span>
                      {item}
                    </p>
                  ))}
                </div>
                <p className="text-[11px] text-white/40 leading-[1.6]">
                  アカデミーの料金は<Link to="/lp/academy" className="text-lp-warm-rose-mid hover:underline">詳細ページ</Link>をご確認ください
                </p>
              </div>
            </div>
          </section>

          {/* ─── CASES ─── */}
          <section className="bg-white">
            <div className="max-w-[880px] mx-auto px-5 md:px-12 py-20">
              <p className="text-[11px] font-bold text-lp-warm-rose tracking-[0.14em] uppercase mb-3 text-center">CASES</p>
              <h2 className="font-noto-serif text-[clamp(22px,3vw,32px)] font-black text-lp-ink leading-[1.5] mb-4 text-center">
                導入事例
              </h2>
              <p className="text-center text-[14px] text-lp-ink2 mb-10 leading-[1.9]">
                入口は「よくある相談」。課題整理から提案・成約へ──事例に共通する構造です。
              </p>
              <div className="grid md:grid-cols-2 gap-5">
                {[
                  {
                    label: '佐賀県・開業18年',
                    title: '解雇相談から、月額10万円の経営伴走へ',
                    items: [
                      '化粧品検査業（40名）の解雇相談が入口',
                      '適正人員数・人件費・賞与算出を提案',
                      '月1回1〜2時間の現場セッションコンサル',
                      '月3.5〜5万円のセッション型コンサル×複数社',
                    ],
                    result: '月額10万円の顧問契約を獲得',
                  },
                  {
                    label: '佐賀県・開業3年',
                    title: '税理士紹介から、月額15万円の経営伴走へ',
                    items: [
                      '税理士からの労務＋給与紹介が入口',
                      '社長ビジョン整理・キャッシュフロー設計まで対応',
                      '月1回2〜3時間の現場セッションコンサル',
                      '建設業（6名）で月額15万円の顧問契約も獲得',
                    ],
                    result: '月額9.5万円＋15万円の複数契約を実現',
                  },
                ].map((c) => (
                  <div key={c.label} className="bg-lp-warm-cream border border-lp-warm-border rounded-lg p-6">
                    <p className="text-[10px] font-bold text-lp-warm-rose tracking-[0.1em] mb-2">{c.label}</p>
                    <h3 className="text-[15px] font-bold text-lp-ink mb-3 leading-[1.4]">{c.title}</h3>
                    <div className="space-y-1.5 mb-3.5">
                      {c.items.map((item) => (
                        <p key={item} className="flex gap-2 text-[12px] text-lp-ink2 leading-[1.6]">
                          <span className="text-lp-warm-rose text-[10px] flex-shrink-0 mt-0.5">▸</span>
                          {item}
                        </p>
                      ))}
                    </div>
                    <div className="bg-white rounded-md px-3.5 py-2.5 text-[12px] text-lp-ink font-bold border-l-[3px] border-lp-warm-rose">
                      {c.result}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ─── FREE SEMINAR ─── */}
          <section className="bg-lp-warm-cream">
            <div className="max-w-[880px] mx-auto px-5 md:px-12 py-16 text-center">
              <p className="text-[11px] font-bold text-lp-warm-rose tracking-[0.14em] uppercase mb-3">FREE SEMINAR</p>
              <h2 className="font-noto-serif text-[clamp(22px,3vw,32px)] font-black text-lp-ink leading-[1.5] mb-4">
                まず、無料セミナーで全体像を掴む
              </h2>
              <p className="text-[14px] text-lp-ink2 leading-[1.9] max-w-[540px] mx-auto mb-8">
                月1回・全4テーマのZoomセミナーを開催しています。どの回からでも参加OK。セミナー後に個別相談もできます。
              </p>
              <Link
                to="/lp/academy/seminar"
                className="inline-block px-9 py-[15px] bg-lp-warm-rose text-white text-[14px] font-bold rounded hover:bg-[#b86666] transition-all hover:-translate-y-px"
              >
                セミナー一覧・申し込みへ
              </Link>
            </div>
          </section>

          {/* ─── CTA ─── */}
          <section className="bg-lp-dark relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_40%_60%,rgba(201,122,122,0.1)_0%,transparent_60%)] pointer-events-none" />
            <div className="relative max-w-[880px] mx-auto px-5 md:px-12 py-24 text-center">
              <h2 className="font-noto-serif text-[clamp(24px,3.5vw,38px)] font-black text-white leading-[1.5] mb-4">
                まず、個別相談から始めてみませんか。
              </h2>
              <p className="text-[14px] text-white/60 leading-[1.9] max-w-[540px] mx-auto mb-9">
                「自分の事務所でも実現できるか？」そんな疑問に、あなたの状況に合わせてお答えします。押し売りは一切ありません。
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                <a
                  href="/contact"
                  className="px-9 py-[15px] bg-lp-warm-rose text-white text-[14px] font-bold rounded hover:bg-[#b86666] transition-all hover:-translate-y-px"
                >
                  個別相談を予約する（無料）
                </a>
                <Link
                  to="/lp/academy/seminar"
                  className="px-9 py-[15px] border-[1.5px] border-white/30 text-white text-[14px] font-bold rounded hover:border-white transition-all"
                >
                  まず無料セミナーに参加する
                </Link>
              </div>
            </div>
          </section>
        </div>
      )}
{/* システム詳細セクション - 共通 */}
<section className="py-20 md:py-32 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6">
    <div className="text-center mb-20">
      <h2 className="text-4xl md:text-5xl font-black text-brown mb-8">
        <span className="font-semibold text-white px-2 py-1 rounded" style={{backgroundColor: '#D8C5C9'}}>SrWith</span>の3つの差別化ポイント
      </h2>
      <p className="text-xl md:text-2xl text-gray-text">
        他のシステムとは一線を画す、労務プラットフォームとしての価値
      </p>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
      {/* 特徴1 */}
      <div className="bg-rose-50 p-10 rounded-3xl shadow-xl border-t-8 border-pink-beige transform hover:scale-105 transition-transform duration-300 text-center">
        <div className="mb-8">
          <div className="w-20 h-20 bg-pink-beige/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">📋</span>
          </div>
          <h3 className="text-2xl font-bold text-brown text-center" style={{minHeight: '4rem', lineHeight: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
事前登録による予防管理</h3>
        </div>
        <div className="space-y-4">
          <div className="bg-rose-100 p-4 rounded-lg min-h-[120px] flex flex-col justify-center">
            <h4 className="font-semibold text-brown mb-2">就業規則の事前登録</h4>
            <p className="text-gray-text text-sm">就業規則をシステムに登録し、勤怠・労働条件を連動</p>
          </div>
          <div className="bg-rose-100 p-4 rounded-lg min-h-[120px] flex flex-col justify-center">
            <h4 className="font-semibold text-brown mb-2">36協定の自動監視</h4>
            <p className="text-gray-text text-sm">締結済みの36協定の内容を基に、残業時間の上限を自動監視・アラート</p>
          </div>
          <div className="bg-rose-100 p-4 rounded-lg min-h-[120px] flex flex-col justify-center">
            <h4 className="font-semibold text-brown mb-2">変形労働時間制対応</h4>
            <p className="text-gray-text text-sm">複雑な変形労働時間制の協定内容も事前登録で、適切な運用を支援</p>
          </div>
        </div>
      </div>

      {/* 特徴2 */}
      <div className="bg-rose-50 p-10 rounded-3xl shadow-xl border-t-8 border-pink-beige transform hover:scale-105 transition-transform duration-300 text-center">
        <div className="mb-8 min-h-[120px] flex flex-col justify-start">
          <div className="w-20 h-20 bg-pink-beige/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🧠</span>
          </div>
          <h3 className="text-2xl font-bold text-brown">社労士の専門知識が詰まったシステム</h3>
        </div>
        <div className="space-y-4">
          <div className="bg-rose-100 p-4 rounded-lg min-h-[120px] flex flex-col justify-center">
            <h4 className="font-semibold text-brown mb-2">17年の実務経験を反映</h4>
            <p className="text-gray-text text-sm">150社以上の支援実績から得られた知見をシステムに組み込み</p>
          </div>
          <div className="bg-rose-100 p-4 rounded-lg min-h-[120px] flex flex-col justify-center">
            <h4 className="font-semibold text-brown mb-2">法改正への迅速対応</h4>
            <p className="text-gray-text text-sm">社労士としての法改正キャッチアップ力で、常に最新の法令に対応</p>
          </div>
          <div className="bg-rose-100 p-4 rounded-lg min-h-[120px] flex flex-col justify-center">
            <h4 className="font-semibold text-brown mb-2">法令対応の課題解決</h4>
            <p className="text-gray-text text-sm">給与計算時、適正な労働時間集計が行えます。</p>
          </div>
        </div>
      </div>

      {/* 特徴3 */}
      <div className="bg-rose-50 p-10 rounded-3xl shadow-xl border-t-8 border-pink-beige transform hover:scale-105 transition-transform duration-300 text-center">
        <div className="mb-8 min-h-[120px] flex flex-col justify-start">
          <div className="w-20 h-20 bg-pink-beige/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🤝</span>
          </div>
          <h3 className="text-2xl font-bold text-brown text-center" style={{minHeight: '4rem', lineHeight: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>社労士と一緒に改善</h3>
        </div>
        <div className="space-y-4">
          <div className="bg-rose-100 p-4 rounded-lg min-h-[120px] flex flex-col justify-center">
            <h4 className="font-semibold text-brown mb-2">継続的なコンサルティング</h4>
            <p className="text-gray-text text-sm">単なるシステム提供ではなく、労務データを活用した経営改善を支援可能に</p>
          </div>
          <div className="bg-rose-100 p-4 rounded-lg min-h-[120px] flex flex-col justify-center">
            <h4 className="font-semibold text-brown mb-2">社労士のシステム保守支援</h4>
            <p className="text-gray-text text-sm">複雑な規程類のデータ入力は社労士が行うため、法的に正しいルール設定が可能</p>
          </div>
          <div className="bg-rose-100 p-4 rounded-lg min-h-[120px] flex flex-col justify-center">
            <h4 className="font-semibold text-brown mb-2">「いい会社」への成長支援</h4>
            <p className="text-gray-text text-sm">法令遵守だけでなく、従業員が安心して働ける環境づくりを支援可能</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* 導入までの流れセクション */}
      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-brown mb-8">
              導入までの流れ
            </h2>
            <p className="text-xl md:text-2xl text-gray-text">
              お問い合わせから導入まで、丁寧にサポートいたします
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="bg-white p-8 rounded-2xl shadow-lg text-center h-full">
                <div className="w-16 h-16 bg-pink-beige text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  1
                </div>
                <h3 className="text-xl font-bold text-brown mb-4">お問い合わせ</h3>
                <p className="text-gray-text">
                  フォームまたはお電話でお気軽にご連絡ください。ご質問だけでも大歓迎です。
                </p>
              </div>
              <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                <svg className="w-8 h-8 text-pink-beige" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                </svg>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="bg-white p-8 rounded-2xl shadow-lg text-center h-full">
                <div className="w-16 h-16 bg-pink-beige text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  2
                </div>
                <h3 className="text-xl font-bold text-brown mb-4">ヒアリング</h3>
                <p className="text-gray-text">
                  現在の労務管理の状況や課題をお伺いし、どのような形でサポートできるかご提案します。
                </p>
              </div>
              <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                <svg className="w-8 h-8 text-pink-beige" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                </svg>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="bg-white p-8 rounded-2xl shadow-lg text-center h-full">
                <div className="w-16 h-16 bg-pink-beige text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  3
                </div>
                <h3 className="text-xl font-bold text-brown mb-4">ご契約・導入</h3>
                <p className="text-gray-text">
                  社労士を通じてご契約。導入後も社労士がしっかりサポートいたします。
                </p>
              </div>
              <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                <svg className="w-8 h-8 text-pink-beige" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                </svg>
              </div>
            </div>

            {/* Step 4 */}
            <div className="relative">
              <div className="bg-white p-8 rounded-2xl shadow-lg text-center h-full">
                <div className="w-16 h-16 bg-pink-beige text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                  4
                </div>
                <h3 className="text-xl font-bold text-brown mb-4">導入・運用開始</h3>
                <p className="text-gray-text">
                  ID発行手続き後、すぐにご利用いただけます。
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/contact"
              className="inline-block bg-pink-beige text-white px-10 py-4 rounded-2xl text-lg font-semibold hover:bg-brown transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              まずはお問い合わせ
            </Link>
          </div>
        </div>
      </section>

      {/* 最終CTA - タブに応じて切り替え */}
      <section className="py-20 md:py-32 bg-pink-beige">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          {activeTab === 'company' ? (
            // 企業向けCTA
            <>
              <h2 className="text-4xl md:text-5xl font-black text-brown mb-8">
                労務管理を「負担」から<br />「競争力」に変革
              </h2>
              <p className="text-xl md:text-2xl text-brown mb-12 leading-relaxed">
                法令遵守と経営改善を同時に実現する<br />
                新しい労務プラットフォーム<span className="font-semibold text-pink-beige px-2 py-0 rounded" style={{backgroundColor: 'white'}}>SrWith</span>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a href={getDownloadUrl('client')} download className={`bg-brown text-white px-10 py-4 rounded-2xl text-lg font-semibold hover:bg-brown/90 transition-all duration-300 transform hover:scale-105 shadow-xl ${getDownloadUrl('client') ? '' : 'opacity-50 pointer-events-none'}`}>
                  企業向け資料をダウンロード
                </a>
              </div>
              
              <p className="text-brown mt-8 text-lg">
                まずはお気軽にお問い合わせください。<br />
                <span className="font-semibold">営業電話は一切いたしません。</span>
              </p>
            </>
          ) : (
            // 社労士向けCTA
            <>
              <h2 className="text-4xl md:text-5xl font-black text-brown mb-8">
                社労士業界の革新を<br />一緒に始めませんか？
              </h2>
              <p className="text-xl md:text-2xl text-brown mb-12 leading-relaxed">
                「いい会社を５万社つくる」ビジョンを<br />
                共に実現するパートナーを募集中
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link to="/contact?purpose=partner" className="bg-brown text-white px-10 py-4 rounded-2xl text-lg font-semibold hover:bg-brown/90 transition-all duration-300 transform hover:scale-105 shadow-xl">
                  パートナー事務所登録
                </Link>
                <a href={getDownloadUrl('sr')} download className={`bg-white text-brown px-10 py-4 rounded-2xl text-lg font-semibold hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 shadow-xl border-2 border-brown ${getDownloadUrl('sr') ? '' : 'opacity-50 pointer-events-none'}`}>
                  社労士向け資料をダウンロード
                </a>
              </div>
              
              <p className="text-brown mt-8 text-lg">
                同業者として、まずは情報交換から始めませんか？<br />
                <span className="font-semibold">業界の未来について一緒に考えましょう。</span>
              </p>
            </>
          )}
        </div>
      </section>

    </div>
  );
};

export default ServicePage;